import { ACTION_TYPES } from '../ActionEconomyManager';
import { normalizeAreaOfEffect } from '../areaOfEffectUtils';

export const COMBAT_ACTION_DOCUMENTATION_PATH = 'Character_Monster_Data_Architecture.md#6-state-machine-and-audit-trail';

/**
 * Action-boundary error with an exact path and remediation contract.
 * No action mechanics are inferred or supplied while constructing this error.
 * rule_reference: AGENTS.md strict runtime validation and actionable errors.
 * date_of_change: 2026-08-21
 */
export class CombatActionContractError extends Error {
  constructor(code, path, expected, received) {
    super(`${path} must be ${expected}. See ${COMBAT_ACTION_DOCUMENTATION_PATH}.`);
    this.name = 'CombatActionContractError';
    this.code = code;
    this.path = path;
    this.expected = expected;
    this.received = received;
    this.documentation = COMBAT_ACTION_DOCUMENTATION_PATH;
  }
}

const asRecord = (value) => (
  value && typeof value === 'object' && !Array.isArray(value) ? value : null
);

const normalizeTargetIds = (value) => {
  if (!Array.isArray(value)) {
    throw new CombatActionContractError(
      'TARGET_IDS_INVALID',
      'request.targetIds',
      'an array of non-empty combatant ID strings',
      value
    );
  }
  value.forEach((entry, index) => {
    if (typeof entry !== 'string' || !entry.trim()) {
      throw new CombatActionContractError(
        'TARGET_ID_INVALID',
        `request.targetIds[${index}]`,
        'a non-empty combatant ID string',
        entry
      );
    }
  });
  return value.map((entry) => entry.trim());
};

export const normalizeCombatActionActivationType = (value) => {
  if (typeof value !== 'string' || !value.trim()) {
    throw new CombatActionContractError(
      'ACTIVATION_TYPE_REQUIRED',
      'actionData.activationType',
      'action, bonus action, reaction, movement, free object, or free',
      value
    );
  }
  const normalized = value.trim();
  if (['action', 'bonus action', 'reaction', 'movement', 'free object', 'free'].includes(normalized)) {
    return normalized;
  }
  throw new CombatActionContractError(
    'ACTIVATION_TYPE_INVALID',
    'actionData.activationType',
    'action, bonus action, reaction, movement, free object, or free',
    value
  );
};

export const requireCombatActionRoute = (actionData) => {
  const route = actionData?.executionRoute;
  if (!['spell', 'attack', 'ability', 'standard'].includes(route)) {
    throw new CombatActionContractError(
      'EXECUTION_ROUTE_REQUIRED',
      'actionData.executionRoute',
      'spell, attack, ability, or standard',
      route
    );
  }
  return route;
};

export const requireCombatActionEconomyType = (actionData) => {
  const normalizedActivation = normalizeCombatActionActivationType(
    actionData?.activationType
  );
  if (normalizedActivation === 'bonus action') return ACTION_TYPES.BONUS_ACTION;
  if (normalizedActivation === 'reaction') return ACTION_TYPES.REACTION;
  if (normalizedActivation === 'movement') return ACTION_TYPES.MOVEMENT;
  if (normalizedActivation === 'free object') return ACTION_TYPES.FREE_OBJECT;
  if (normalizedActivation === 'free') return ACTION_TYPES.FREE_ACTION;
  return ACTION_TYPES.ACTION;
};

const normalizeActionPayload = (value) => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    throw new CombatActionContractError(
      'ACTION_DATA_REQUIRED',
      'actionData',
      'a structured combat action object',
      value
    );
  }
  if (typeof value.name !== 'string' || !value.name.trim()) {
    throw new CombatActionContractError('ACTION_NAME_REQUIRED', 'actionData.name', 'a non-empty string', value.name);
  }
  const activationType = normalizeCombatActionActivationType(value.activationType);
  requireCombatActionRoute(value);
  const targetRequirement = value.targetRequirement;
  if (!['none', 'self', 'creature', 'object', 'point', 'area', 'trigger'].includes(targetRequirement)) {
    throw new CombatActionContractError(
      'TARGET_REQUIREMENT_REQUIRED',
      'actionData.targetRequirement',
      'none, self, creature, object, point, area, or trigger',
      targetRequirement
    );
  }
  return { ...value, name: value.name.trim(), activationType, targetRequirement };
};

const resolveAreaOfEffect = (actionData) => {
  const suppliedArea = actionData?.areaOfEffect;
  if (!suppliedArea) return null;
  const normalized = normalizeAreaOfEffect(suppliedArea);
  if (!normalized) {
    throw new CombatActionContractError(
      'AREA_OF_EFFECT_INVALID',
      'actionData.areaOfEffect',
      'a supported shape with a positive numeric size',
      suppliedArea
    );
  }
  return normalized;
};

export const normalizeCombatActionRequest = (
  requestOrAttackerId,
  options = {}
) => {
  const requestObject = asRecord(requestOrAttackerId);
  if (!requestObject) {
    throw new CombatActionContractError(
      'ACTION_REQUEST_REQUIRED',
      'request',
      'an object containing attackerId and actionData',
      requestOrAttackerId
    );
  }

  const attacker = options.attacker || requestObject.attacker || null;
  const attackerId = requestObject.attackerId;
  if (typeof attackerId !== 'string' || !attackerId.trim()) {
    throw new CombatActionContractError('ATTACKER_ID_REQUIRED', 'request.attackerId', 'a non-empty string', attackerId);
  }
  const rawAction = requestObject.actionData;
  const actionData = normalizeActionPayload(rawAction);
  const gmOverride = requestObject.gmOverride || options.gmOverride || null;
  const actionName = actionData.name;
  const targetIds = normalizeTargetIds(requestObject.targetIds || []);
  const areaOfEffect = resolveAreaOfEffect(actionData);
  if (actionData.targetRequirement === 'area' && !areaOfEffect) {
    throw new CombatActionContractError(
      'AREA_OF_EFFECT_REQUIRED',
      'actionData.areaOfEffect',
      'a supported shape with a positive numeric size for an area-targeted action',
      actionData.areaOfEffect
    );
  }
  const route = requireCombatActionRoute(actionData);
  const engineActionType = requireCombatActionEconomyType(actionData);
  const hasExplicitTargets = targetIds.length > 0;
  const hasNamedObject = actionData.targetRequirement === 'object'
    && typeof actionData.objectName === 'string'
    && Boolean(actionData.objectName.trim());
  const targetIsRequired = ['creature', 'object', 'point', 'area'].includes(actionData.targetRequirement);
  const requiresBattleRoomTargeting = Boolean(
    targetIsRequired
    && !hasExplicitTargets
    && !areaOfEffect
    && !hasNamedObject
  );

  return {
    __normalizedCombatActionRequest: true,
    attackerId,
    combatantId: attackerId,
    attacker,
    actionName,
    actionData,
    definition: actionData,
    targetIds,
    selectedTargetIds: targetIds,
    hasExplicitTargets,
    route,
    activationType: actionData.activationType,
    engineActionType,
    areaOfEffect,
    hasAreaOfEffect: Boolean(areaOfEffect),
    requiresBattleRoomTargeting,
    // This is an intent only. BattleRoom authenticates the GM role and creates
    // the engine override object at the execution boundary.
    // date_of_change: 2026-07-27
    gmOverride,
    metadata: requestObject.metadata || null
  };
};

export const buildActionEconomyRequest = (normalizedRequest, options = {}) => {
  const request = normalizedRequest?.__normalizedCombatActionRequest
    ? normalizedRequest
    : normalizeCombatActionRequest(normalizedRequest, options);
  const actionName = request.actionName;
  const movementCost = options.movementCost ?? request.actionData?.movementCost ?? 0;
  const resolvedAttackerId = request.attackerId;
  const resolvedAttackerName = options.attacker?.name || request.attacker?.name || null;
  const attacker = options.attacker || request.attacker || null;
  if (!attacker || typeof attacker !== 'object') {
    throw new CombatActionContractError('ATTACKER_REQUIRED', 'request.attacker', 'the canonical combatant snapshot', attacker);
  }
  if (attacker.id !== resolvedAttackerId) {
    throw new CombatActionContractError('ATTACKER_ID_MISMATCH', 'request.attacker.id', resolvedAttackerId, attacker.id);
  }
  if (typeof resolvedAttackerName !== 'string' || !resolvedAttackerName.trim()) {
    throw new CombatActionContractError('ATTACKER_NAME_REQUIRED', 'request.attacker.name', 'a non-empty string', resolvedAttackerName);
  }

  return {
    attackerId: resolvedAttackerId,
    combatantId: resolvedAttackerId,
    actionType: options.actionType || request.engineActionType,
    actionName,
    attacker,
    targets: Array.isArray(options.targets) ? options.targets : [],
    actionData: {
      ...request.actionData,
      name: actionName
    },
    movementCost
  };
};

const CombatActionRequestNormalizer = {
  normalizeCombatActionRequest,
  normalizeCombatActionActivationType,
  requireCombatActionRoute,
  requireCombatActionEconomyType,
  buildActionEconomyRequest
};

export default CombatActionRequestNormalizer;
